// Phase T2.0A Prompt 1 — Universal event registration dialog.
//
// One reusable modal for both Tournaments and Round Robins. Renders:
//   Singles → confirm button only.
//   Doubles → helper copy + partner dropdown with these options:
//     1. Looking for a Partner            (→ backend 'looking_for_partner')
//     2. Waiting for Partner to Register  (→ backend 'not_registered')
//     3. Divider
//     4. Alphabetical (First → Last) eligible members  (→ 'confirmed')
//
// Uses the mobile-safe DialogContent / Header / Body / Footer primitives so
// the confirm button is always reachable on iPhone, safe-area padded, and
// keyboard-safe.
//
// Composition rules are enforced server-side; this client just filters the
// partner list (already server-filtered) and disables the CTA while the
// request is in flight.

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Users, User, HelpCircle } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { registrationAPI } from '../../lib/registrationApi';

// Constants for the two placeholder rows at the top of the dropdown.
const PARTNER_NOT_REGISTERED  = '__partner_not_registered__';
const PARTNER_LOOKING         = '__looking_for_partner__';

// Composition label used in the header copy.
const COMP_LABEL = {
  open: 'Open',
  mens: "Men's",
  womens: "Women's",
  mixed: 'Mixed',
};

export default function EventRegistrationDialog({
  open, onOpenChange,
  eventKind,          // 'tournament' | 'round_robin'
  eventId,
  eventName,
  eventType,          // 'singles' | 'doubles'
  composition,        // 'open' | 'mens' | 'womens' | 'mixed'
  onRegistered,       // callback after successful register
}) {
  const [partners, setPartners] = useState(null);   // null = loading, [] = loaded
  const [selection, setSelection] = useState('');   // profile id OR sentinel
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isDoubles = eventType === 'doubles';

  // Load the eligible-partner list every time the dialog opens.
  useEffect(() => {
    if (!open || !isDoubles) return;
    setPartners(null);
    setError('');
    setSelection('');
    const fetcher = eventKind === 'tournament'
      ? registrationAPI.tournamentEligiblePartners
      : registrationAPI.rrEligiblePartners;
    fetcher(eventId)
      .then((r) => setPartners(r.data.partners || []))
      .catch((e) => {
        setPartners([]);
        setError(e.response?.data?.detail?.message || 'Could not load eligible members.');
      });
  }, [open, isDoubles, eventKind, eventId]);

  // Alphabetical grouping is already handled by the backend (First → Last).
  const groupedList = useMemo(() => partners || [], [partners]);

  async function handleConfirm() {
    setBusy(true);
    setError('');
    const body = {};
    if (isDoubles) {
      if (selection === PARTNER_NOT_REGISTERED) body.partner_status = 'not_registered';
      else if (selection === PARTNER_LOOKING)   body.partner_status = 'looking_for_partner';
      else if (selection)                       body.partner_profile_id = selection;
      else                                      body.partner_status = 'not_registered';
    }
    try {
      const fn = eventKind === 'tournament'
        ? registrationAPI.tournamentRegister
        : registrationAPI.rrRegister;
      await fn(eventId, body);
      toast.success('You’re registered.');
      onRegistered?.();
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      const msg = e.response?.data?.detail?.message
        || 'Registration failed. Please try again.';
      // Friendlier phrasing for the two most-common race codes.
      if (code === 'registration_conflict') {
        setError('That player was just selected by another team, or you are already registered. Please refresh and choose another partner.');
      } else if (code === 'capacity_full') {
        // This event just filled up. Reload the parent so the CTA can flip
        // to "Event Full → Join Alternate List" and close the dialog.
        toast.error('This event just filled up. Please try the alternate list.');
        onRegistered?.();
        onOpenChange(false);
        return;
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  const confirmDisabled = busy || (isDoubles && partners === null);

  const CompIcon =
    composition === 'mixed'  ? Users
      : composition === 'mens' || composition === 'womens' ? Users
      : User;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="event-registration-dialog"
        className="sm:max-w-md flex flex-col"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-wider text-[#6a766e]">
            <CompIcon size={14} />
            <span>{COMP_LABEL[composition] || composition} · {eventType === 'singles' ? 'Singles' : 'Doubles'}</span>
          </div>
          <DialogTitle className="pr-6">{eventName || 'Register for event'}</DialogTitle>
          <DialogDescription>
            {isDoubles
              ? 'Choose your partner. You can update this later while registration is open.'
              : 'Confirm your registration for this event.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isDoubles && (
            <div className="space-y-1.5" data-testid="partner-picker">
              <label htmlFor="partner-select" className="text-[12px] uppercase tracking-wider text-[#6a766e]">
                Partner
              </label>
              <p className="text-[12px] text-[#6a766e] leading-snug" data-testid="partner-helper">
                Choose your partner from the list. If they aren’t listed yet, they need to register first.
              </p>
              <select
                id="partner-select"
                data-testid="partner-select"
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
                className="w-full h-11 rounded-lg border border-[#e3dcd1] bg-white px-3 text-[15px] text-[#013523] focus:outline-none focus:border-[#015e3f] focus:ring-2 focus:ring-[#015e3f]/20"
              >
                <option value="" data-testid="opt-choose">— Choose —</option>
                <option value={PARTNER_LOOKING}        data-testid="opt-looking">Looking for a Partner</option>
                <option value={PARTNER_NOT_REGISTERED} data-testid="opt-not-registered">Waiting for Partner to Register</option>
                <option disabled>──────────────</option>
                {partners === null ? (
                  <option disabled>Loading eligible members…</option>
                ) : (
                  groupedList.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                      data-testid={`opt-partner-${p.id}`}
                    >
                      {p.first_name} {p.last_name}
                    </option>
                  ))
                )}
              </select>
              {partners !== null && groupedList.length === 0 && (
                <div className="text-[12px] text-[#8a6d1a] pt-1" data-testid="no-eligible">
                  No other registered players are unpaired yet. You can still register
                  as <em>Looking for a Partner</em> or <em>Waiting for Partner to Register</em>.
                </div>
              )}
            </div>
          )}

          {!isDoubles && (
            <div className="text-[13.5px] text-[#2a332c]">
              You’ll be added to the entry list immediately. You can withdraw
              yourself from Settings or from this page while registration is
              open.
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-lg bg-[#fdf1f1] border border-[#f0c7c7] px-3 py-2 text-[12.5px] text-[#8a1c1c]"
                 data-testid="registration-error">
              {error}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="register-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="bg-[#015e3f] text-white hover:bg-[#013523]"
            data-testid="register-confirm"
          >
            {busy ? 'Registering…' : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
